import { useCallback, useMemo, useRef, useState } from "react";

const DEFAULT_KEY = "__default__";

const toKey = (value) => {
  if (value === undefined || value === null || value === "") return DEFAULT_KEY;
  return String(value);
};

/**
 * Guards actions against repeated taps/clicks.
 *
 * Features:
 * - Single-flight per key (drops calls while same key is in-flight)
 * - Optional minimum interval per key (throttle-like protection)
 * - Async/sync action support
 */
export const useActionGuard = (
  action,
  {
    keyBy = () => DEFAULT_KEY,
    minIntervalMs = 300,
    onSkipped,
  } = {},
) => {
  const inFlightRef = useRef(new Set());
  const lastStartedAtRef = useRef(new Map());
  const [pendingKeys, setPendingKeys] = useState(new Set());

  const run = useCallback(
    async (...args) => {
      const key = toKey(keyBy(...args));
      const now = Date.now();

      if (inFlightRef.current.has(key)) {
        onSkipped?.("in_flight", key);
        return undefined;
      }

      const lastStartedAt = lastStartedAtRef.current.get(key) || 0;
      if (minIntervalMs > 0 && now - lastStartedAt < minIntervalMs) {
        onSkipped?.("rate_limited", key);
        return undefined;
      }

      inFlightRef.current.add(key);
      lastStartedAtRef.current.set(key, now);
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      try {
        return await Promise.resolve(action(...args));
      } finally {
        inFlightRef.current.delete(key);
        setPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [action, keyBy, minIntervalMs, onSkipped],
  );

  const isRunning = pendingKeys.size > 0;

  const isPending = useCallback(
    (key = DEFAULT_KEY) => pendingKeys.has(toKey(key)),
    [pendingKeys],
  );

  return useMemo(
    () => ({
      run,
      isRunning,
      isPending,
    }),
    [run, isRunning, isPending],
  );
};

export default useActionGuard;
